import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, onSnapshot, collection, query, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import { auth, db, functions } from "../firebase";
import TopBar from "../components/common/TopBar";
import LivoraModalCard from "../components/common/LivoraModalCard";
import LivoraToast from "../components/common/LivoraToast";
import "./SupportChatPage.css";
import { formatDateTimeEn } from "../utils/digits";

function formatDate(value) {
  try {
    if (!value) return "";
    if (typeof value?.toDate === "function") {
      return formatDateTimeEn(value.toDate(), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "";
  } catch {
    return "";
  }
}

function getStatusMeta(status, t) {
  switch (status) {
    case "open":
      return {
        label: t("support.status.open", { defaultValue: "Open" }),
        className: "open",
      };
    case "in_progress":
      return {
        label: t("support.status.in_progress", { defaultValue: "In progress" }),
        className: "in-progress",
      };
    case "waiting_user":
      return {
        label: t("support.status.waiting_user", { defaultValue: "Waiting for you" }),
        className: "waiting-user",
      };
    case "resolved":
      return {
        label: t("support.status.resolved", { defaultValue: "Resolved" }),
        className: "resolved",
      };
    case "closed":
      return {
        label: t("support.status.closed", { defaultValue: "Closed" }),
        className: "closed",
      };
    default:
      return {
        label: status || t("support.status.unknown", { defaultValue: "Unknown" }),
        className: "unknown",
      };
  }
}

export default function SupportChatPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { t } = useTranslation();

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);

  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);

  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "",
    cancelText: "",
    variant: "danger",
    onConfirm: null,
  });

  const user = auth.currentUser;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, show: false }));
  };

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmText: "",
      cancelText: "",
      variant: "danger",
      onConfirm: null,
    });
  };

  useEffect(() => {
    if (!ticketId) return;

    const ticketRef = doc(db, "supportTickets", ticketId);
    const unsub = onSnapshot(
      ticketRef,
      (snap) => {
        if (!snap.exists()) {
          setTicket(null);
          setLoadingTicket(false);
          return;
        }

        setTicket({
          id: snap.id,
          ...snap.data(),
        });
        setLoadingTicket(false);
      },
      (error) => {
        console.error("support ticket read error:", error);
        setTicket(null);
        setLoadingTicket(false);
        showToast(
          t("support.ticket_read_error", {
            defaultValue: "Could not load the ticket.",
          }),
          "error"
        );
      }
    );

    return () => unsub();
  }, [ticketId, t]);

  useEffect(() => {
    if (!ticketId) return;

    const q = query(
      collection(db, "supportTickets", ticketId, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setMessages(rows);
        setLoadingMessages(false);
      },
      (error) => {
        console.error("support messages read error:", error);
        setMessages([]);
        setLoadingMessages(false);
        showToast(
          t("support.messages_read_error", {
            defaultValue: "Could not load messages.",
          }),
          "error"
        );
      }
    );

    return () => unsub();
  }, [ticketId, t]);

  useEffect(() => {
    if (!messages.length) return;
    const timer = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 60);
    return () => clearTimeout(timer);
  }, [messages]);

  const statusMeta = useMemo(
    () => getStatusMeta(ticket?.status, t),
    [ticket?.status, t]
  );

  const canReply = useMemo(() => {
    if (!ticket) return false;
    return ["open", "in_progress", "waiting_user"].includes(ticket.status);
  }, [ticket]);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachment(file);
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if (!ticketId || !user) return;

    if (!text.trim() && !attachment) {
      showToast(
        t("support.message_or_attachment_required", {
          defaultValue: "Write a message or add an attachment first.",
        }),
        "info"
      );
      return;
    }

    setSending(true);
    try {
      const sendSupportMessage = httpsCallable(functions, "sendSupportMessage");

      const res = await sendSupportMessage({
        ticketId,
        text: text.trim(),
        attachmentName: attachment?.name || null,
        attachmentSize: attachment?.size || null,
        attachmentUrl: null,
        attachmentPath: null,
      });

      console.log("support message sent:", res.data);

      setText("");
      removeAttachment();

      showToast(
        t("support.message_sent_success", {
          defaultValue: "Reply sent successfully.",
        }),
        "success"
      );
    } catch (e) {
      console.error("send support message error:", e);
      showToast(
        t("support.send_error", {
          defaultValue: "Something went wrong while sending the message.",
        }),
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  const doCloseTicket = async () => {
    if (!ticketId) return;

    setClosing(true);
    try {
      const closeSupportTicket = httpsCallable(functions, "closeSupportTicket");
      const res = await closeSupportTicket({ ticketId });

      console.log("support ticket closed:", res.data);

      showToast(
        t("support.ticket_closed_success", {
          defaultValue: "Ticket closed successfully.",
        }),
        "success"
      );
    } catch (e) {
      console.error("close support ticket error:", e);
      showToast(
        t("support.close_error", {
          defaultValue: "Could not close the ticket.",
        }),
        "error"
      );
    } finally {
      setClosing(false);
      closeDialog();
    }
  };

  const handleCloseTicket = () => {
    setDialog({
      open: true,
      title: t("support.close_ticket", { defaultValue: "Close ticket" }),
      message: t("support.confirm_close_ticket", {
        defaultValue:
          "Do you want to close this ticket? After closing it, you cannot send new messages unless support reopens it later.",
      }),
      confirmText: t("support.close_ticket", { defaultValue: "Close ticket" }),
      cancelText: t("common.cancel", { defaultValue: "Cancel" }),
      variant: "danger",
      onConfirm: doCloseTicket,
    });
  };

  return (
    <div className="support-chat-root">
      <TopBar
        title={t("support.ticket_details", { defaultValue: "Ticket details" })}
        onBack={() => navigate(-1)}
      />

      <div className="support-chat-page">
        {loadingTicket ? (
          <div className="support-chat-state-card">
            {t("common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : !ticket ? (
          <div className="support-chat-state-card">
            {t("support.ticket_not_found", {
              defaultValue: "This ticket does not exist or you cannot access it.",
            })}
          </div>
        ) : (
          <>
            <div className="support-chat-ticket-card">
              <div className="support-chat-ticket-top">
                <div className="support-chat-ticket-left">
                  <div className="support-chat-ticket-label">
                    {t("support.ticket", { defaultValue: "Support ticket" })}
                  </div>
                  <div className="support-chat-ticket-subject">
                    {ticket.subject ||
                      t("support.no_subject", { defaultValue: "No subject" })}
                  </div>
                </div>

                <div className={`support-chat-status ${statusMeta.className}`}>
                  {statusMeta.label}
                </div>
              </div>

              <div className="support-chat-meta-grid">
                <div className="support-chat-meta-item">
                  <span className="support-chat-meta-key">
                    {t("support.category", { defaultValue: "Category" })}
                  </span>
                  <span className="support-chat-meta-value">
                    {ticket.category ||
                      t("support.category_unknown", { defaultValue: "Unknown" })}
                  </span>
                </div>

                <div className="support-chat-meta-item">
                  <span className="support-chat-meta-key">
                    {t("support.priority", { defaultValue: "Priority" })}
                  </span>
                  <span className="support-chat-meta-value">
                    {ticket.priority
                      ? t(`support.priority_levels.${ticket.priority}`, {
                          defaultValue: String(ticket.priority),
                        })
                      : t("support.priority_levels.normal", { defaultValue: "normal" })}
                  </span>
                </div>

                <div className="support-chat-meta-item">
                  <span className="support-chat-meta-key">
                    {t("support.created_at", { defaultValue: "Created at" })}
                  </span>
                  <span className="support-chat-meta-value">
                    {formatDate(ticket.createdAt)}
                  </span>
                </div>

                <div className="support-chat-meta-item">
                  <span className="support-chat-meta-key">
                    {t("support.last_reply", { defaultValue: "Last reply" })}
                  </span>
                  <span className="support-chat-meta-value">
                    {formatDate(ticket.lastReplyAt || ticket.updatedAt)}
                  </span>
                </div>
              </div>

              {ticket.attachmentName && (
                <div className="support-chat-ticket-attachment">
                  <div className="support-chat-ticket-attachment-title">
                    {t("support.first_attachment", {
                      defaultValue: "First attachment",
                    })}
                  </div>
                  <div className="support-chat-ticket-attachment-name">
                    {ticket.attachmentName}
                  </div>
                  {!!ticket.attachmentSize && (
                    <div className="support-chat-ticket-attachment-size">
                      {(ticket.attachmentSize / 1024).toFixed(0)} KB
                    </div>
                  )}
                </div>
              )}

              {canReply && (
                <button
                  className="support-chat-close-btn"
                  onClick={handleCloseTicket}
                  disabled={closing}
                >
                  {closing
                    ? t("common.loading", { defaultValue: "Loading..." })
                    : t("support.close_ticket", { defaultValue: "Close ticket" })}
                </button>
              )}
            </div>

            <div className="support-chat-messages-card">
              <div className="support-chat-messages-header">
                {t("support.messages", { defaultValue: "Conversation" })}
              </div>

              {loadingMessages ? (
                <div className="support-chat-messages-state">
                  {t("common.loading", { defaultValue: "Loading..." })}
                </div>
              ) : messages.length === 0 ? (
                <div className="support-chat-messages-state">
                  {t("support.no_messages_yet", {
                    defaultValue: "No messages yet.",
                  })}
                </div>
              ) : (
                <div className="support-chat-messages-list">
                  {messages.map((msg) => {
                    const isUser = msg.senderRole === "user";
                    const isSystem = msg.senderRole === "system";

                    return (
                      <div
                        key={msg.id}
                        className={[
                          "support-chat-bubble-wrap",
                          isSystem
                            ? "system"
                            : isUser
                            ? "user"
                            : "admin",
                        ].join(" ")}
                      >
                        <div
                          className={[
                            "support-chat-bubble",
                            isSystem
                              ? "system"
                              : isUser
                              ? "user"
                              : "admin",
                          ].join(" ")}
                        >
                          <div className="support-chat-bubble-role">
                            {isSystem
                              ? t("support.system", { defaultValue: "System" })
                              : isUser
                              ? t("support.you", { defaultValue: "You" })
                              : t("support.team", { defaultValue: "Support" })}
                          </div>

                          {!!msg.text && (
                            <div className="support-chat-bubble-text">
                              {msg.text}
                            </div>
                          )}

                          {!!msg.attachmentName && (
                            <div className="support-chat-bubble-attachment">
                              <div className="support-chat-bubble-attachment-name">
                                {msg.attachmentName}
                              </div>
                              {!!msg.attachmentSize && (
                                <div className="support-chat-bubble-attachment-size">
                                  {(msg.attachmentSize / 1024).toFixed(0)} KB
                                </div>
                              )}
                            </div>
                          )}

                          <div className="support-chat-bubble-time">
                            {formatDate(msg.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
              )}
            </div>

            {canReply ? (
              <div className="support-chat-compose-card">
                <div className="support-chat-compose-title">
                  {t("support.reply_to_ticket", { defaultValue: "Send reply" })}
                </div>

                <textarea
                  className="support-chat-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("support.reply_placeholder", {
                    defaultValue: "Write your reply here...",
                  })}
                  maxLength={2000}
                />

                <div className="support-chat-compose-footer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="support-chat-hidden-file"
                    onChange={onPickFile}
                  />

                  <button
                    type="button"
                    className="support-chat-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("support.attach_image", { defaultValue: "Attach image" })}
                  </button>

                  <button
                    type="button"
                    className="support-chat-send-btn"
                    onClick={handleSend}
                    disabled={sending}
                  >
                    {sending
                      ? t("common.saving", { defaultValue: "Sending..." })
                      : t("support.send", { defaultValue: "Send" })}
                  </button>
                </div>

                {attachment && (
                  <div className="support-chat-selected-file">
                    <div className="support-chat-selected-file-name">
                      {attachment.name}
                    </div>
                    <div className="support-chat-selected-file-size">
                      {(attachment.size / 1024).toFixed(0)} KB
                    </div>
                    <button
                      type="button"
                      className="support-chat-selected-file-remove"
                      onClick={removeAttachment}
                      aria-label={t("support.remove_attachment", {
                        defaultValue: "Remove attachment",
                      })}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="support-chat-closed-note">
                {t("support.ticket_closed_note", {
                  defaultValue: "This ticket is closed and no new messages can be added.",
                })}
              </div>
            )}
          </>
        )}

        <LivoraModalCard
          dialog={dialog}
          onClose={closeDialog}
        />

        <LivoraToast toast={toast} onClose={closeToast} />
      </div>
    </div>
  );
}
